import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import { usePageMeta } from "@/hooks/usePageMeta";
import { getMediaById, getNewsById, getLatestNews } from "@/utils/media";
import { typeLabels } from "@/utils/labels";

export default function NewsDetailPage() {
  const { id = "" } = useParams();
  const news = getNewsById(id);
  const relatedMedia = news ? getMediaById(news.relatedMediaId) : undefined;
  const otherNews = getLatestNews(4).filter((item) => item.id !== id).slice(0, 3);

  usePageMeta({
    title: news ? `${news.title} - 影视资讯 - 17C.COM` : "影视资讯 - 17C.COM",
    description: news
      ? `${news.excerpt} 阅读完整影视资讯与影评，尽在 17C.COM。`
      : "该资讯内容不存在或已下架。",
    keywords: news
      ? `${news.category},影视资讯,影评,${news.title},17C.COM`
      : "影视资讯,17C.COM",
  });

  if (!news) {
    return (
      <div className="mx-auto w-full max-w-7xl px-4 py-16 md:px-6">
        <EmptyState
          icon="ri-article-line"
          title="该资讯内容不存在或已下架"
          description="你访问的资讯可能已被移除，去看看其他影视动态吧。"
          action={
            <Link
              to="/news"
              className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-700"
            >
              <i className="ri-newspaper-line" aria-hidden="true" />
              返回资讯列表
            </Link>
          }
        />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: "影视资讯", to: "/news" },
          { label: news.category },
        ]}
      />

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
        <article className="lg:col-span-2">
          <span className="inline-flex items-center gap-1.5 rounded bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-800">
            <i className="ri-price-tag-3-line" aria-hidden="true" />
            {news.category}
          </span>
          <h1 className="mt-3 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-3xl">
            {news.title}
          </h1>
          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-background-200 pb-4 text-sm text-foreground-400">
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line" aria-hidden="true" />
              {news.date}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line" aria-hidden="true" />
              {news.author}
            </span>
          </div>

          <div className="mt-6 aspect-[16/9] w-full overflow-hidden rounded-lg bg-background-200">
            <img
              src={news.cover}
              alt={news.title}
              title={news.title}
              className="h-full w-full object-cover object-top"
            />
          </div>

          <div className="mt-6 space-y-4">
            <p className="text-base font-medium leading-relaxed text-foreground-800">
              {news.excerpt}
            </p>
            {news.content.map((paragraph, index) => (
              <p
                key={index}
                className="text-sm leading-relaxed text-foreground-600 md:text-base"
              >
                {paragraph}
              </p>
            ))}
          </div>

          {relatedMedia && (
            <Link
              to={`/detail/${relatedMedia.id}`}
              className="group mt-8 flex items-center gap-4 rounded-lg border border-background-200 bg-background-100 p-4 transition-colors hover:border-primary-200"
            >
              <div className="h-24 w-20 shrink-0 overflow-hidden rounded-md bg-background-200">
                <img
                  src={relatedMedia.cover}
                  alt={relatedMedia.title}
                  title={relatedMedia.title}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <div className="min-w-0">
                <span className="text-xs font-medium text-accent-600">
                  关联影视 · {typeLabels[relatedMedia.type]}
                </span>
                <h3 className="mt-1 font-heading text-base font-bold text-foreground-950 transition-colors group-hover:text-primary-600">
                  {relatedMedia.title}
                </h3>
                <p className="mt-1 line-clamp-2 text-sm text-foreground-500">
                  {relatedMedia.synopsis}
                </p>
              </div>
              <i
                className="ri-arrow-right-line ml-auto hidden shrink-0 text-xl text-foreground-300 transition-transform group-hover:translate-x-1 group-hover:text-primary-500 sm:block"
                aria-hidden="true"
              />
            </Link>
          )}
        </article>

        <aside>
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
              <i className="ri-newspaper-line text-primary-500" aria-hidden="true" />
              相关阅读
            </h2>
            <ul className="mt-4 space-y-3">
              {otherNews.map((item) => (
                <li key={item.id}>
                  <Link
                    to={`/news/${item.id}`}
                    className="group block rounded-md border border-background-200 p-3 transition-colors hover:border-primary-200"
                  >
                    <h3 className="line-clamp-2 text-sm font-medium leading-snug text-foreground-800 transition-colors group-hover:text-primary-600">
                      {item.title}
                    </h3>
                    <p className="mt-1 text-xs text-foreground-400">
                      {item.category} · {item.date}
                    </p>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
}